import type { FieldGroup } from './types.js';

/**
 * Desired political position — exhaustive list of elected, appointed, and
 * party positions in the Indian political system. Organized by level
 * (rural local body → urban local body → state → national → party roles
 * → constitutional). Respondents select one aspiration.
 */
export const desiredPoliticalPositionOptions = [
  // ── Rural local body ──
  { value: 'ward_member', label: 'Ward Member / Panch' },
  { value: 'sarpanch', label: 'Sarpanch / Village Head' },
  { value: 'panchayat_samiti_member', label: 'Panchayat Samiti Member' },
  { value: 'block_pramukh', label: 'Block Pramukh / Panchayat Samiti Chairperson' },
  { value: 'zila_panchayat_member', label: 'Zila Panchayat / Parishad Member' },
  { value: 'zila_pramukh', label: 'Zila Panchayat Chairperson / Zila Pramukh' },

  // ── Urban local body ──
  { value: 'municipal_councillor', label: 'Municipal Councillor / Corporator' },
  { value: 'deputy_mayor', label: 'Deputy Mayor' },
  { value: 'mayor', label: 'Mayor' },
  { value: 'municipal_chairperson', label: 'Municipal Chairperson / Nagar Adhyaksha' },

  // ── State level ──
  { value: 'mla', label: 'MLA (State Legislative Assembly)' },
  { value: 'mlc', label: 'MLC (State Legislative Council)' },
  { value: 'state_minister', label: 'State Minister (Cabinet / MoS)' },
  { value: 'deputy_cm', label: 'Deputy Chief Minister' },
  { value: 'chief_minister', label: 'Chief Minister' },
  { value: 'state_speaker', label: 'Speaker / Deputy Speaker (State Assembly)' },
  { value: 'state_opposition_leader', label: 'Leader of Opposition (State)' },

  // ── National / Parliament ──
  { value: 'mp_lok_sabha', label: 'MP (Lok Sabha)' },
  { value: 'mp_rajya_sabha', label: 'MP (Rajya Sabha)' },
  { value: 'union_minister', label: 'Union Minister (Cabinet / MoS)' },
  { value: 'deputy_pm', label: 'Deputy Prime Minister' },
  { value: 'prime_minister', label: 'Prime Minister' },
  { value: 'lok_sabha_speaker', label: 'Speaker / Deputy Speaker (Lok Sabha)' },
  { value: 'opposition_leader', label: 'Leader of Opposition (Lok Sabha)' },

  // ── Party roles ──
  { value: 'booth_worker', label: 'Booth-level Worker' },
  { value: 'mandal_president', label: 'Mandal / Block Party President' },
  { value: 'district_party_president', label: 'District Party President' },
  { value: 'state_party_president', label: 'State Party President' },
  { value: 'national_party_president', label: 'National Party President' },
  { value: 'party_spokesperson', label: 'Party Spokesperson' },

  // ── Constitutional (highest) ──
  { value: 'governor', label: 'Governor (State)' },
  { value: 'vice_president', label: 'Vice President of India' },
  { value: 'president', label: 'President of India' },

  // ── Meta ──
  { value: 'none', label: "None — I'm not interested in office" },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Leadership & political skillset — exhaustive list of skills relevant to
 * Indian political, civic, and activist work. Grouped below thematically
 * (communication, organizing, strategy, interpersonal, legal/admin,
 * financial, digital, field work) but flattened in the exported array.
 */
export const leadershipSkillsetOptions = [
  // ── Communication ──
  { value: 'public_speaking', label: 'Public Speaking' },
  { value: 'media_handling', label: 'Media Handling / Press Relations' },
  { value: 'writing_drafting', label: 'Writing & Drafting (speeches, statements)' },
  { value: 'multilingual_communication', label: 'Multilingual Communication' },
  { value: 'debate_argumentation', label: 'Debate & Argumentation' },
  { value: 'storytelling', label: 'Storytelling & Narrative Building' },
  { value: 'social_media_management', label: 'Social Media Management' },

  // ── Organizing & Mobilization ──
  { value: 'community_organizing', label: 'Community Organizing' },
  { value: 'grassroots_mobilization', label: 'Grassroots Mobilization' },
  { value: 'campaign_management', label: 'Campaign Management' },
  { value: 'event_management', label: 'Event Management' },
  { value: 'volunteer_management', label: 'Volunteer Management' },
  { value: 'crowd_management', label: 'Crowd Management' },
  { value: 'door_to_door_canvassing', label: 'Door-to-door Canvassing' },

  // ── Strategy & Policy ──
  { value: 'strategic_planning', label: 'Strategic Planning' },
  { value: 'policy_research', label: 'Policy Research / Drafting' },
  { value: 'political_analysis', label: 'Political Analysis' },
  { value: 'data_analytics', label: 'Data Analytics / Voter Analytics' },
  { value: 'manifesto_drafting', label: 'Manifesto Drafting' },

  // ── Interpersonal ──
  { value: 'conflict_resolution', label: 'Conflict Resolution' },
  { value: 'negotiation_mediation', label: 'Negotiation & Mediation' },
  { value: 'coalition_building', label: 'Coalition Building' },
  { value: 'team_leadership', label: 'Team Leadership & Management' },
  { value: 'mentorship', label: 'Mentorship & Training' },

  // ── Legal & Administrative ──
  { value: 'legal_awareness', label: 'Legal & Election-Law Awareness' },
  { value: 'rti_filing', label: 'RTI Filing' },
  { value: 'bureaucratic_navigation', label: 'Bureaucratic Navigation' },
  { value: 'compliance_documentation', label: 'Compliance & Documentation' },

  // ── Financial ──
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'budget_management', label: 'Budget & Financial Management' },

  // ── Digital ──
  { value: 'digital_campaigning', label: 'Digital Campaigning' },
  { value: 'whatsapp_campaigning', label: 'WhatsApp / Messaging Campaigns' },
  { value: 'content_creation', label: 'Content Creation (Video / Design)' },
  { value: 'voter_targeting', label: 'Data-driven Voter Targeting' },

  // ── Field / Ground ──
  { value: 'booth_management', label: 'Booth / Polling Station Management' },
  { value: 'voter_registration_drives', label: 'Voter Registration Drives' },
  { value: 'crisis_management', label: 'Crisis Management' },
] as const;

export const participateOptions = [
  { value: 'yes_definitely', label: 'Yes, definitely!' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'not_sure', label: 'Not sure yet' },
  { value: 'no', label: 'No' },
] as const;

export const politicalExposureOptions = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
  { value: 'very_high', label: 'Very High' },
] as const;

export const comfortWithVisibilityOptions = [
  { value: 'very_comfortable', label: 'Very comfortable' },
  { value: 'somewhat', label: 'Somewhat' },
  { value: 'prefer_behind_scenes', label: 'Prefer behind the scenes' },
  { value: 'not_comfortable', label: 'Not comfortable' },
] as const;

export const socialInfluenceOptions = [
  { value: 'very_influential', label: 'Very influential' },
  { value: 'somewhat', label: 'Somewhat' },
  { value: 'average', label: 'Average' },
  { value: 'low', label: 'Low' },
  { value: 'not_sure', label: 'Not sure' },
] as const;

export const pastExperienceOptions = [
  { value: 'yes_party_work', label: 'Yes — Party work' },
  { value: 'yes_social_work', label: 'Yes — Social work / NGO' },
  { value: 'yes_student_politics', label: 'Yes — Student politics' },
  { value: 'no_experience', label: 'No experience' },
  { value: 'other', label: 'Other' },
] as const;

export const leadershipAmbitions: FieldGroup = {
  key: 'leadership_ambitions',
  label: 'Leadership & Ambitions',
  step: 4,
  description: 'Your desire to lead, political aspirations, and skills.',
  fields: [
    {
      order: 1,
      key: 'participateInRevolution',
      label: 'Participate in Revolution',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...participateOptions],
    },
    {
      order: 2,
      key: 'leadershipSkillset',
      label: 'Leadership Skills',
      type: 'multi_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...leadershipSkillsetOptions],
    },
    {
      order: 3,
      key: 'politicalExposure',
      label: 'Political Exposure',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...politicalExposureOptions],
    },
    {
      order: 4,
      key: 'comfortWithVisibility',
      label: 'Comfort with Visibility',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...comfortWithVisibilityOptions],
    },
    {
      order: 5,
      key: 'socialInfluenceLevel',
      label: 'Social Influence Level',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...socialInfluenceOptions],
    },
    {
      order: 6,
      key: 'pastExperience',
      label: 'Past Experience',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...pastExperienceOptions],
    },
    {
      order: 7,
      key: 'desiredPoliticalPosition',
      label: 'Desired Political Position',
      type: 'single_select',
      step: 4,
      isOnboarding: true,
      optional: true,
      options: [...desiredPoliticalPositionOptions],
    },
  ],
};
